import { cn } from "../lib/utils";
import { motion, useReducedMotion } from "framer-motion";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
function AnimatedGridPattern({ width = 22, height = 22, x = -1, y = -1, strokeDasharray = 0, numSquares = 30, maxOpacity = 0.28, durationSeconds = 1.2, repeatDelaySeconds = 0, }) {
    const shouldReduceMotion = useReducedMotion();
    const id = useId();
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [squares, setSquares] = useState([]);
    const getPos = useCallback(() => {
        return [
            Math.floor((Math.random() * dimensions.width) / width),
            Math.floor((Math.random() * dimensions.height) / height),
        ];
    }, [dimensions.height, dimensions.width, height, width]);
    const generateSquares = useCallback((count) => Array.from({ length: count }, (_, i) => ({
        id: i,
        pos: getPos(),
        iteration: 0,
    })), [getPos]);
    const updateSquarePosition = useCallback((squareId) => {
        setSquares((curr) => {
            const current = curr[squareId];
            if (!current || current.id !== squareId) {
                return curr;
            }
            const next = curr.slice();
            next[squareId] = {
                ...current,
                pos: getPos(),
                iteration: current.iteration + 1,
            };
            return next;
        });
    }, [getPos]);
    useEffect(() => {
        if (dimensions.width && dimensions.height) {
            setSquares(generateSquares(numSquares));
        }
    }, [dimensions.width, dimensions.height, generateSquares, numSquares]);
    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setDimensions((curr) => {
                    const w = entry.contentRect.width;
                    const h = entry.contentRect.height;
                    if (curr.width === w && curr.height === h) {
                        return curr;
                    }
                    return { width: w, height: h };
                });
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return (React.createElement("svg", { ref: containerRef, "aria-hidden": "true", style: {
            pointerEvents: "none",
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            color: "rgb(195,195,205)",
            stroke: "rgba(156,163,175,0.12)",
            fill: "none",
        } },
        React.createElement("defs", null,
            React.createElement("pattern", { id: id, width: width, height: height, patternUnits: "userSpaceOnUse", x: x, y: y },
                React.createElement("path", { d: `M.5 ${height}V.5H${width}`, fill: "none", strokeDasharray: strokeDasharray }))),
        React.createElement("rect", { width: "100%", height: "100%", fill: `url(#${id})` }),
        React.createElement("svg", { x: x, y: y, style: { overflow: "visible" } }, squares.map(({ pos: [sx, sy], id: sid, iteration }) => (React.createElement(motion.rect, { key: `${sid}-${iteration}`, initial: { opacity: 0 }, animate: { opacity: shouldReduceMotion ? 0 : maxOpacity }, transition: shouldReduceMotion
                ? { duration: 0 }
                : {
                    duration: durationSeconds,
                    repeat: 1,
                    delay: sid * 0.05,
                    repeatType: "reverse",
                    repeatDelay: repeatDelaySeconds,
                    ease: [0.645, 0.045, 0.355, 1],
                }, onAnimationComplete: shouldReduceMotion ? undefined : () => updateSquarePosition(sid), width: width - 1, height: height - 1, x: sx * width + 1, y: sy * height + 1, fill: "currentColor", strokeWidth: "0" }))))));
}
function AnimatedDots() {
    const [n, setN] = useState(1);
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }
        const t = setInterval(() => setN((c) => (c % 3) + 1), 500);
        return () => clearInterval(t);
    }, []);
    return (React.createElement("span", { "aria-hidden": "true", style: { display: "inline-block", width: "1.2ch", textAlign: "left" } }, ".".repeat(n)));
}
const EASE_OUT_QUART = "cubic-bezier(0.165, 0.84, 0.44, 1)";
export function ImageGenerationPlaceholder({ src, alt = "Generated image", label = "Creating image", size = 260, fill = false, className, }) {
    const shouldReduceMotion = useReducedMotion();
    const [gridOpacity, setGridOpacity] = useState(1);
    const [imgMounted, setImgMounted] = useState(false);
    const [imgOpacity, setImgOpacity] = useState(0);
    const rafRef = useRef(null);
    useEffect(() => {
        if (!src) {
            return;
        }
        setGridOpacity(0);
        const delay = shouldReduceMotion ? 0 : 120;
        const t = setTimeout(() => {
            setImgMounted(true);
            rafRef.current = requestAnimationFrame(() => setImgOpacity(1));
        }, delay);
        return () => {
            clearTimeout(t);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [src, shouldReduceMotion]);
    return (React.createElement("div", { className: cn("overflow-hidden rounded-2xl", "bg-muted-background", fill ? "absolute inset-0" : "relative shrink-0", className), style: fill ? undefined : { width: size, height: size } },
        React.createElement("div", { style: {
                position: "absolute",
                inset: 0,
                opacity: gridOpacity,
                transition: shouldReduceMotion
                    ? "none"
                    : `opacity 220ms ${EASE_OUT_QUART}`,
                pointerEvents: gridOpacity > 0 ? "auto" : "none",
            } },
            React.createElement("span", { className: cn("absolute top-3 left-3.5 z-10", "text-xs font-medium pointer-events-none", "text-muted-foreground", "motion-safe:animate-opacity-pulse") },
                label,
                React.createElement(AnimatedDots, null)),
            React.createElement(AnimatedGridPattern, null)),
        imgMounted && src && (React.createElement("img", { src: src, alt: alt, draggable: false, style: {
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: imgOpacity,
                transition: shouldReduceMotion
                    ? "none"
                    : `opacity 350ms ${EASE_OUT_QUART}`,
            } }))));
}
//# sourceMappingURL=ImageGenerationPlaceholder.js.map